// frontend/src/ui/MobileMenu.tsx
import React, { useRef } from "react";
import { NavLink } from "react-router-dom";

type MenuItem =
  | { type: "link"; to: string; label: string }
  | { type: "external"; href: string; label: string }
  | { type: "action"; onClick: () => void; label: string };

export default function MobileMenu({
  open,
  close,
  items,
  title = "Menu",
}: {
  open: boolean;
  close: () => void;
  items: MenuItem[];
  title?: string;
}) {
  const startX = useRef<number | null>(null);

  const onTouchStart = (e: React.TouchEvent) => {
    startX.current = e.touches?.[0]?.clientX ?? null;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    if (startX.current == null) return;
    const dx = (e.changedTouches?.[0]?.clientX ?? startX.current) - startX.current;
    if (dx < -50) close();
    startX.current = null;
  };

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/30" onClick={close} />

      {/* Panel */}
      <aside
        role="dialog"
        aria-label="Menu"
        className={`fixed z-50 top-0 left-0 h-full w-full bg-white shadow-xl transform transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-4 h-14 flex items-center justify-between">
          <div className="text-lg font-semibold">{title}</div>
          <button
            onClick={close}
            aria-label="Close menu"
            className="text-sm text-gray-500 inline-flex items-center gap-1"
          >
            <svg viewBox="0 0 24 24" className="w-4 h-4 stroke-current" fill="none">
              <path d="M6 6l12 12M6 18L18 6" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
            Close
          </button>
        </div>

        {/* Body: one item per row */}
        <nav className="px-2 py-3">
          <ul className="space-y-1">
            {items.map((it, i) => {
              if (it.type === "link") {
                return (
                  <li key={`${it.to}-${i}`}>
                    <NavLink
                      to={it.to}
                      onClick={close}
                      className={({ isActive }) =>
                        `block px-3 py-3 rounded-lg text-base transition ${
                          isActive ? "bg-blue-50 text-blue-700" : "hover:bg-gray-50"
                        }`
                      }
                      aria-current={({ isActive }) => (isActive ? "page" : undefined)}
                    >
                      {it.label}
                    </NavLink>
                  </li>
                );
              }
              if (it.type === "external") {
                return (
                  <li key={`${it.href}-${i}`}>
                    <a
                      href={it.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={close}
                      className="block px-3 py-3 rounded-lg text-base hover:bg-gray-50 transition"
                    >
                      <span className="inline-flex items-center gap-2">
                        <span>{it.label}</span>
                        <svg
                          viewBox="0 0 24 24"
                          className="w-3.5 h-3.5 stroke-current opacity-60"
                          fill="none"
                        >
                          <path d="M18 13v6H6V6h6" strokeWidth="1.6" />
                          <path d="M14 4h6v6" strokeWidth="1.6" />
                          <path d="M10 14L20 4" strokeWidth="1.6" />
                        </svg>
                      </span>
                    </a>
                  </li>
                );
              }
              // action
              return (
                <li key={`action-${i}`}>
                  <button
                    onClick={() => {
                      close();
                      it.onClick();
                    }}
                    className="w-full text-left px-3 py-3 rounded-lg text-base hover:bg-gray-50 transition"
                  >
                    {it.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>
    </>
  );
}
